import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Calendar from 'react-calendar';
import { apiService } from '../../api/apiService';
import type { Training } from '../../types';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Clock, Play } from 'lucide-react';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import 'react-calendar/dist/Calendar.css';
import './UserCalendar.css';

export const UserCalendar: React.FC = () => {
  const [date, setDate] = useState(new Date());
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [todayTrainings, setTodayTrainings] = useState<Training[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    loadTrainings();
  }, []);

  useEffect(() => {
    filterTodayTrainings();
  }, [trainings, date]);

  const loadTrainings = async () => {
    try {
      const data = await apiService.getTrainings();
      // Sort trainings by date (most recent first)
      setTrainings(data.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error('Error loading trainings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterTodayTrainings = () => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const filtered = trainings.filter((training) => {
      const trainingDate = training.date.split('T')[0]; // Extraer solo la parte de la fecha
      return trainingDate === dateStr;
    });
    // Sort filtered trainings by date (most recent first)
    const sorted = filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    setTodayTrainings(sorted);
  };

  const tileClassName = ({ date }: { date: Date }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const hasTraining = trainings.some((training) => {
      const trainingDate = training.date.split('T')[0];
      return trainingDate === dateStr;
    });
    return hasTraining ? 'has-training' : '';
  };

  const tileContent = ({ date }: { date: Date }) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayTrainings = trainings.filter((training) => {
      const trainingDate = training.date.split('T')[0];
      return trainingDate === dateStr;
    });

    if (dayTrainings.length === 0) return null;

    return (
      <div className="tile-trainings">
        {dayTrainings.map((training) => (
          <div
            key={training.id}
            className="tile-training-item"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/training/${training.id}`);
            }}
          >
            {training.title}
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return <div className="text-center p-4">{t('loading')}</div>;
  }

  return (
    <div className="user-calendar-page">
      <h1 className="page-title">{t('calendar')}</h1>

      <div className="calendar-container">
        <Card>
          <Calendar
            onChange={(value) => setDate(value as Date)}
            value={date}
            locale={i18n.language === 'es' ? 'es' : 'en'}
            tileClassName={tileClassName}
            tileContent={tileContent}
          />
        </Card>

        <div className="trainings-list">
          <h2 className="trainings-title">
            {i18n.language === 'es'
              ? `Entrenamientos del ${format(date, "d 'de' MMMM", { locale: es })}`
              : `Trainings for ${format(date, 'MMMM d', { locale: enUS })}`
            }
          </h2>
          <p className="trainings-count">
            {todayTrainings.length} {i18n.language === 'es'
              ? (todayTrainings.length === 1 ? 'entrenamiento programado' : 'entrenamientos programados')
              : (todayTrainings.length === 1 ? 'training scheduled' : 'trainings scheduled')
            }
          </p>

          {todayTrainings.length === 0 ? (
            <Card>
              <p className="no-trainings">{t('noTrainingsToday')}</p>
            </Card>
          ) : (
            <div className="trainings-grid">
              {todayTrainings.map((training) => (
                <Card
                  key={training.id}
                  className="training-detail-card"
                >
                  <div className="training-detail-header">
                    <div className="training-detail-title-section">
                      <h3 className="training-detail-title">{training.title}</h3>
                      <span className="training-detail-badge">
                        {format(new Date(training.date), 'dd/MM')}
                      </span>
                    </div>
                    <Button
                      onClick={() => navigate(`/user/training/run/${training.id}`)}
                      className="start-training-button-header"
                      variant="primary"
                    >
                      <Play size={18} />
                      {t('startTraining')}
                    </Button>
                  </div>
                  <p className="training-detail-time">
                    <Clock size={16} />
                    {format(new Date(training.date), 'HH:mm')}
                  </p>
                  {training.observations && (
                    <p className="training-detail-description">{training.observations}</p>
                  )}

                  <div className="training-detail-separator" />

                  {training.warmup_section && training.warmup_section.exercises.length > 0 && (
                    <div className="training-section warmup">
                      <div className="training-section-header">
                        <h4 className="training-section-title">{t('warmup')}</h4>
                        <div className="training-section-params">
                          {training.warmup_section.params.seconds && (
                            <span className="param-badge">{training.warmup_section.params.seconds}s</span>
                          )}
                          {training.warmup_section.params.rest_between_exercises && (
                            <span className="param-badge">
                              <Clock size={14} /> {training.warmup_section.params.rest_between_exercises}s
                            </span>
                          )}
                        </div>
                      </div>
                      <ul className="training-section-list">
                        {training.warmup_section.exercises.map((exercise, idx) => (
                          <li key={idx} className="training-section-item">
                            {exercise.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {training.main_section && training.main_section.exercises.length > 0 && (
                    <div className="training-section main">
                      <div className="training-section-header">
                        <h4 className="training-section-title">{t('mainPart')}</h4>
                        <div className="training-section-params">
                          {training.main_section.params.series && (
                            <span className="param-badge">{training.main_section.params.series} series</span>
                          )}
                          {training.main_section.params.seconds && (
                            <span className="param-badge">{training.main_section.params.seconds}s</span>
                          )}
                          {training.main_section.params.rest_between_series && (
                            <span className="param-badge">
                              <Clock size={14} /> {training.main_section.params.rest_between_series}s
                            </span>
                          )}
                          {training.main_section.params.rest_between_exercises && (
                            <span className="param-badge">
                              <Clock size={14} /> {training.main_section.params.rest_between_exercises}s descanso
                            </span>
                          )}
                        </div>
                      </div>
                      <ul className="training-section-list">
                        {training.main_section.exercises.map((exercise, idx) => (
                          <li key={idx} className="training-section-item">
                            {exercise.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {training.cooldown_section && training.cooldown_section.exercises.length > 0 && (
                    <div className="training-section cooldown">
                      <div className="training-section-header">
                        <h4 className="training-section-title">{t('cooldown')}</h4>
                        <div className="training-section-params">
                          {training.cooldown_section.params.seconds && (
                            <span className="param-badge">{training.cooldown_section.params.seconds}s</span>
                          )}
                          {training.cooldown_section.params.rest_between_exercises && (
                            <span className="param-badge">
                              <Clock size={14} /> {training.cooldown_section.params.rest_between_exercises}s
                            </span>
                          )}
                        </div>
                      </div>
                      <ul className="training-section-list">
                        {training.cooldown_section.exercises.map((exercise, idx) => (
                          <li key={idx} className="training-section-item">
                            {exercise.name}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
